import { SERVER_ADDRESS_HTTP } from "./constants";

/**
 * Secret icons: typing a secret code in a "Your name" field unlocks extra icons. The server checks the code and
 * serves the images; the browser remembers the codes that worked.
 */

const STORAGE_KEY = "shoSecretUnlocks";

/** The shape of a code ("xxxxx-xxxxx"); the server decides whether it is a real one. */
export const SECRET_CODE_PATTERN = /^[a-z0-9]{5}-[a-z0-9]{5}$/i;
/** The shape of the id of a secret icon. */
export const SECRET_ICON_PATTERN = /^s_[0-9a-f]{12}$/;

export type SecretUnlock = { code: string; label: string; icons: string[] };

export function secretIconUrl(id: string): string {
  return SERVER_ADDRESS_HTTP + "/secret-icon?id=" + encodeURIComponent(id);
}

function readUnlocks(): SecretUnlock[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function writeUnlocks(unlocks: SecretUnlock[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(unlocks));
  } catch (e) {
    // Storage can be blocked; the icons then stay unlocked until the page is closed.
    memoryUnlocks = unlocks;
  }
}

let memoryUnlocks: SecretUnlock[] = [];

function allUnlocks(): SecretUnlock[] {
  const stored = readUnlocks();
  return stored.length > 0 ? stored : memoryUnlocks;
}

/** The ids of all secret icons this browser has unlocked. */
export function unlockedSecretIcons(): string[] {
  const ids: string[] = [];
  allUnlocks().forEach((u) =>
    u.icons.forEach((id) => {
      if (!ids.includes(id)) ids.push(id);
    })
  );
  return ids;
}

/** The code that unlocked the given icon, to send along when picking it. */
export function codeForSecretIcon(id: string): string | undefined {
  const unlock = allUnlocks().find((u) => u.icons.includes(id));
  return unlock ? unlock.code : undefined;
}

/**
 * Asks the server whether the code is valid. If so, remembers it and returns what it unlocked, otherwise null.
 */
export async function redeemSecretCode(code: string): Promise<SecretUnlock | null> {
  const normalized = code.trim().toLowerCase();
  try {
    const response = await fetch(
      SERVER_ADDRESS_HTTP + "/unlock?code=" + encodeURIComponent(normalized)
    );
    if (!response.ok) return null;
    const body = await response.json();
    const unlock: SecretUnlock = { code: normalized, label: body.label, icons: body.icons };
    writeUnlocks([...allUnlocks().filter((u) => u.code !== normalized), unlock]);
    return unlock;
  } catch (e) {
    return null;
  }
}
